/**
 * Patient Data Refresh Hook - Smart Summary Refresh Status Management
 *
 * ARCHITECTURE OVERVIEW:
 * ======================
 *
 * This module manages Smart Summary refresh status using a WebSocket-first approach
 * with graceful fallback to polling when WebSocket data is unavailable.
 *
 * DATA FLOW:
 * ----------
 * 1. PRIMARY: WebSocket messages provide real-time refresh status + history
 *    - Status updates: "started", intermediate steps, "finished"
 *    - Full history: Array of {status, timestamp} entries
 *    - No API calls needed for status updates when WebSocket is connected
 *
 * 2. FALLBACK: Background process API (only when WebSocket history unavailable)
 *    - Used when: WebSocket disconnected, history field missing, initial page load
 *    - Polling mechanism: Batched queries for multiple patients (7s intervals)
 *    - Max polling duration: 10 minutes
 *
 * KEY COMPONENTS:
 * ---------------
 * - globalRefreshState: In-memory Map tracking refresh status per patient
 * - handleGlobalSmartSummaryStatus: Global WebSocket event handler
 * - updateGlobalStateFromBulkData: Syncs API data to global state (fallback)
 * - BackgroundProcessStatus: Displays status + tooltip with history
 *
 * STATE SYNCHRONIZATION:
 * ----------------------
 * - Event-based listener system (globalStateListeners)
 * - Notifies all subscribed components when state changes
 * - Enables header status updates regardless of current page
 *
 * BENEFITS:
 * ---------
 * ✓ Eliminates stale UI states (real-time WebSocket updates)
 * ✓ Reduces API load (WebSocket-first, polling only as fallback)
 * ✓ Scalable for long-running async workflows
 * ✓ Patient-level status tracking with full step visibility
 *
 * @module usePatientDataRefresh
 */

import { useState, useEffect, useMemo, useRef, useContext } from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { refreshSmartSummary } from "apiClients/patients";
import { useWebSocketHooks } from "../useWebSocketHooks";
import { useWebSocketEvent } from "../useWebSocketEvent";
import { useBackgroundProcess } from "contexts/BackgroundProcessContext";
import { useSelector } from "react-redux";
import { useWebSocket } from "contexts/WebSocketContext";
import { fetchBulkActiveBackgroundProcesses, fetchBackgroundProcesses, DATA_READY_MARKER } from "apiClients/backgroundProcess";
import { FeatureFlagsContext } from "contexts/FeatureFlagsContext";
import { falseRefreshFailuresSuppressFeature, isFeatureEnabled } from "utils/featureFlags";
import { emitRefreshMeasurement } from "services/monitoring";

export interface HistoryEntry {
  status: string;
  timestamp: string;
  duration_ms?: number;
}

export interface RefreshState {
  isRefreshing: boolean;
  startTime: number;
  history: HistoryEntry[];
  patientName: string;
  processId?: string; // NEW: Background process ID from WebSocket
  currentStep?: string; // NEW: Current step from WebSocket
}

/**
 * Server-side lifecycle of an api_backgroundprocess row. Already serialized by the backend and
 * present on BackgroundProcess, but the UI only ever read the derived `is_active`
 * (which is `state in (NOT_STARTED, RUNNING)`) and so could not tell "queued" from "running", or
 * a genuine ERROR from an EXPIRED row the sweep stamped simply because the queue was backed up.
 */
export type BackendProcessState = "NOT_STARTED" | "RUNNING" | "DONE" | "ERROR" | "EXPIRED";

export interface CompletedRefresh {
  patientId: string;
  patientName: string;
  processId?: string;
  startTime: number;
  completedAt: number;
  duration: number;
  history: HistoryEntry[];
  // "timeout" is retained only for the suppressed (pre-fix) path and for entries already
  // persisted in localStorage. "unknown" is the new non-alarming terminal state: the refresh
  // outran our polling ceiling, which is not evidence that it failed.
  status: "success" | "error" | "timeout" | "unknown";
  errorMessage?: string;
  backendState?: BackendProcessState;
}

/**
 * Whether a completed refresh should offer a Retry CTA (and count toward the red badge).
 *
 * Retrying is not free: it POSTs /api/patients/refresh_smart_summary/, which creates another
 * background process and enqueues another SQS message. Offering it for a refresh that is merely
 * slow makes a backlog worse and duplicates an LLM generation that was going to land anyway, so
 * the unsuppressed path gates strictly on evidence of actual failure.
 *
 * EXPIRED is deliberately NOT retryable yet. Today the backend stamps EXPIRED from a fixed 10
 * minute TTL measured from creation, so under load it means "still queued", not "lost" -- treating
 * it as failure would just move the false alarm later. Once the backend TTL becomes
 * inactivity-based, EXPIRED becomes trustworthy and can be added here.
 */
export const isRetryableRefresh = (refresh: CompletedRefresh, suppressed: boolean): boolean => {
  if (suppressed) {
    return refresh.status === "error" || refresh.status === "timeout";
  }
  return refresh.status === "error" || refresh.backendState === "ERROR";
};

// Simple global state to track which patients are currently being refreshed
const globalRefreshState = new Map<string, RefreshState>();

// LocalStorage key for completed refreshes.
// Bumped to _v2: the v1 list is full of "timeout" entries that were never failures,
// and because the only code path that removes an entry is a Retry click, they would otherwise
// persist as red badges indefinitely -- pushing users into the very retry the fix removes.
// Deliberately NOT behind the suppress flag: flipping the kill switch must not resurrect them.
const COMPLETED_REFRESHES_KEY = "nephrolytics_completed_refreshes_v2";
const LEGACY_COMPLETED_REFRESHES_KEY = "nephrolytics_completed_refreshes";

// The list previously grew without bound, with a `history` array embedded per entry. Once it
// exceeds the ~5MB localStorage quota setItem throws, which saveCompletedRefreshes catches and
// logs -- so persistence would silently stop working. Bound it by both count and age.
const MAX_COMPLETED_REFRESHES = 50;
const COMPLETED_REFRESH_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// Drop entries that are too old, then cap the list. Input is assumed newest-first.
const pruneCompletedRefreshes = (refreshes: CompletedRefresh[]): CompletedRefresh[] => {
  const cutoff = Date.now() - COMPLETED_REFRESH_TTL_MS;
  return refreshes.filter((r) => (r?.completedAt ?? 0) > cutoff).slice(0, MAX_COMPLETED_REFRESHES);
};

// Load completed refreshes from localStorage (persists across page refreshes)
const loadCompletedRefreshes = (): CompletedRefresh[] => {
  try {
    // Best-effort cleanup of the previous list so it does not sit in storage forever.
    localStorage.removeItem(LEGACY_COMPLETED_REFRESHES_KEY);
    const stored = localStorage.getItem(COMPLETED_REFRESHES_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? pruneCompletedRefreshes(parsed) : [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to load completed refreshes from localStorage:", error);
    return [];
  }
};

// Save completed refreshes to localStorage
const saveCompletedRefreshes = (refreshes: CompletedRefresh[]) => {
  try {
    localStorage.setItem(COMPLETED_REFRESHES_KEY, JSON.stringify(refreshes));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to save completed refreshes to localStorage:", error);
  }
};

// Global array to store completed refreshes (persisted in localStorage)
const completedRefreshProcesses: CompletedRefresh[] = loadCompletedRefreshes();

// DEV ONLY: expose for console testing
if (import.meta.env.DEV) {
  (window as any).__devInjectCompletedRefresh = (refresh: CompletedRefresh) => {
    completedRefreshProcesses.unshift(refresh);
    notifyStateChange();
  };
}

// LocalStorage key for patient name cache
const PATIENT_NAME_CACHE_KEY = "nephrolytics_patient_name_cache";

// Load patient name cache from localStorage
const loadPatientNameCache = (): Record<string, string> => {
  try {
    const stored = localStorage.getItem(PATIENT_NAME_CACHE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to load patient name cache from localStorage:", error);
    return {};
  }
};

// Save patient name cache to localStorage
const savePatientNameCache = (cache: Record<string, string>) => {
  try {
    localStorage.setItem(PATIENT_NAME_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to save patient name cache to localStorage:", error);
  }
};

// Global patient name cache (persisted in localStorage)
// Maps patientId -> patientName to recover names after page reload
const patientNameCache: Record<string, string> = loadPatientNameCache();

// Helper to get patient name with fallback chain:
// 1. In-memory globalRefreshState
// 2. localStorage patientNameCache
// 3. Fallback to "Patient {id}"
const getPatientName = (patientId: string | number): string => {
  const patientIdStr = String(patientId);

  // Try in-memory state first
  const inMemoryName = globalRefreshState.get(patientIdStr)?.patientName;
  if (inMemoryName && !inMemoryName.startsWith("Patient ")) {
    return inMemoryName;
  }

  // Try localStorage cache
  const cachedName = patientNameCache[patientIdStr];
  if (cachedName) {
    return cachedName;
  }

  // Fallback
  return `Patient ${patientIdStr}`;
};

// Helper to cache patient name when we have it
const cachePatientName = (patientId: string | number, patientName: string) => {
  const patientIdStr = String(patientId);

  // Don't cache fallback names
  if (patientName.startsWith("Patient ")) {
    return;
  }

  patientNameCache[patientIdStr] = patientName;
  savePatientNameCache(patientNameCache);
};

// Global polling state to track which patients are being polled
const globalPollingState = new Map(); // Map<patientId, { intervalId: number, startTime: number }>

// Global batched polling - single interval that polls all patients at once
let globalBatchPollingIntervalId: any = null;
const POLLING_INTERVAL = 7000;
const MAX_POLLING_DURATION = 10 * 60 * 1000;
// Ceiling for the unsuppressed path. Chosen from production: on the worst business day measured, the
// slowest end-to-end refresh was ~24 minutes (queue wait, not processing -- processing exceeded
// 10 minutes in 0.22% of refreshes over 30 days). 45 minutes sits comfortably beyond that, so we
// stop watching only when something really is unaccounted for.
const EXTENDED_MAX_POLLING_DURATION = 45 * 60 * 1000;

// Throttle map to track last fetch time per patient (for webhook-triggered updates)
const lastFetchTimeMap = new Map<string, number>();
const inFlightFetchSet = new Set<string>(); // Track in-flight fetches to prevent concurrent requests
const FETCH_THROTTLE_INTERVAL = 3000; // 3 seconds

// Event-based notification system for global state changes
const globalStateListeners = new Set();

// Helper function to compare history arrays
const areHistoriesEqual = (a: HistoryEntry[], b: HistoryEntry[]): boolean => {
  if (a.length !== b.length) return false;
  return a.every(
    (entry, index) =>
      entry.status === b[index].status && entry.timestamp === b[index].timestamp && entry.duration_ms === b[index].duration_ms
  );
};

// Notify all components of state changes
const notifyStateChange = () => {
  globalStateListeners.forEach((listener: any) => {
    try {
      listener();
    } catch {
      // Silently handle listener errors to prevent breaking other listeners
    }
  });
};

// ---------------------------------------------------------------------------
// data_ready recognition
// ---------------------------------------------------------------------------

// Patients whose data_ready signal has already triggered the early EMR refetch for the CURRENT refresh.
// Lives at module scope (independent of WebSocket lifecycle) so the WebSocket and polling paths dedup
// against the same flag. It deliberately SURVIVES cleanupPatientState -- a late data_ready arriving after
// "finished" must not re-fire the refetch -- and is cleared only when a NEW refresh starts (resetDataReady).
const dataReadyHandled = new Set<string>();

// The background process each patient's data_ready latch belongs to. "started" is emitted by the SUMMARY
// stage, which runs after the EMR fetch has already signalled data_ready for the SAME process, so the
// process id is what separates that from a genuinely new refresh (NKF-1449). Cleared by resetDataReady.
const dataReadyProcess = new Map<string, string>();

// Patients whose data_ready EMR refetch has actually LANDED in cache (or hit the safety-valve timeout) for
// the CURRENT refresh. Distinct from dataReadyHandled (which flips the instant the signal is received, before
// the refetch resolves): this flips only once the fresh EMR data is back, so a consumer can hold its loading
// placeholder until then instead of revealing the stale pre-refresh cache and flashing to fresh query-by-query.
// One-shot per refresh -- never recomputed from a live isFetching flag -- so a later background refetch during
// the minutes-long summary window cannot flip it back and re-hide the form (the stuck-loading regression).
// Cleared by resetDataReady when a new refresh begins.
const emrDataLandedSet = new Set<string>();

// Pending safety-valve timers keyed by patient, so a superseding refresh (resetDataReady) can cancel a stale one.
const emrDataLandTimers = new Map<string, ReturnType<typeof setTimeout>>();

// Safety valve: reveal even if a data_ready refetch hangs (server accepts the request but never responds during
// the heavy refresh window) rather than pinning the placeholder forever -- the failure mode tracked. Set
// above the query retry envelope (global retry: failureCount < 3 with exponential backoff) and at the low end of
// the placeholder's own "30-60s" estimate, so it only trips on a genuine hang, never a slow-but-completing refetch.
const EMR_DATA_LAND_TIMEOUT = 30000;

const clearEmrDataLandTimer = (patientIdStr: string) => {
  const timer = emrDataLandTimers.get(patientIdStr);
  if (timer) {
    clearTimeout(timer);
    emrDataLandTimers.delete(patientIdStr);
  }
};

// Query keys refetched when EMR data lands (data_ready). EMR/PIF data only -- deliberately excludes the
// summary ("latestSmartNote"), which is repopulated by "finished", so the early refetch cannot cache the
// mid-regen NULL summary Saya writes when it starts the fan-out.
const buildDataReadyQueryKeys = (patientId: string | number) => [
  ["patientData", patientId],
  ["medications", patientId],
  ["allergies", patientId],
  ["externalLabs", patientId],
  ["allmedicalReports", patientId],
  ["pharmacies", patientId],
  ["payers", patientId],
  ["pifDocuments", patientId],
  ["documentsSinceLastEncounter", patientId],
  ["clinicalFacts", patientId],
  ["kfreScores", patientId],
];

// Whether a background process carries a persisted data_ready step. The step is durable on the process
// history, unlike the websocket event, which is only ever delivered live and never replayed -- so this is
// what lets a page that mounted mid-refresh recover a data_ready that fired before it existed (NKF-1449).
const hasDataReadyStep = (process: any): boolean =>
  Array.isArray(process?.history) && process.history.some((step: any) => step && step.code === DATA_READY_MARKER);

// Recognize the data_ready signal once per refresh, on whichever transport delivers it first (WebSocket or
// polling). Refetches EMR/PIF data early WITHOUT ending the refresh -- the spinner, polling, and final
// cleanup stay owned by "finished" (consumers decide what to render on data_ready). notifyStateChange lets
// any mounted hook surface dataReady in its return value.
const handleDataReady = (patientId: string | number, queryClient: any, processId?: string | number) => {
  const patientIdStr = String(patientId);
  if (!queryClient || dataReadyHandled.has(patientIdStr)) {
    return;
  }
  dataReadyHandled.add(patientIdStr);
  if (processId) {
    dataReadyProcess.set(patientIdStr, String(processId));
  }
  // Refetch the EMR/PIF data and wait for it to land before signalling emrDataLanded. invalidateQueries
  // returns a promise that settles once the active refetches complete; consumers that reveal on landing (the
  // PIF placeholder) therefore hold their loading state until the fresh data is in cache, instead of flashing
  // the stale pre-refresh cache and then swapping to fresh values query-by-query. allSettled (not all) so a
  // single failing EMR endpoint still lands the rest -- a rejected refetch must not force the full timeout wait.
  // Keyed on patientIdStr, not the raw argument: react-query compares query keys structurally, so
  // ["medications", 12345] does not match the ["medications", "12345"] every consumer builds from the
  // string route param. The websocket paths pass details.patient_id, which is a number -- so before this
  // the event invalidated nothing, resolved instantly, and revealed the form on stale data.
  const refetches = Promise.allSettled(
    buildDataReadyQueryKeys(patientIdStr).map((queryKey) => queryClient.invalidateQueries({ queryKey }))
  );
  // Latch emrDataLanded once every refetch settles, OR after the safety-valve timeout -- whichever is first.
  const markEmrDataLanded = () => {
    // Ignore a stale promise/timer whose refresh has already been superseded (resetDataReady clears the flag).
    if (!dataReadyHandled.has(patientIdStr) || emrDataLandedSet.has(patientIdStr)) {
      return;
    }
    emrDataLandedSet.add(patientIdStr);
    clearEmrDataLandTimer(patientIdStr);
    notifyStateChange();
  };
  refetches.then(markEmrDataLanded);
  emrDataLandTimers.set(patientIdStr, setTimeout(markEmrDataLanded, EMR_DATA_LAND_TIMEOUT));
  notifyStateChange();
};

// Clear the data_ready dedup flag when a fresh refresh begins so the next refresh can signal again.
// Intentionally NOT called from cleanupPatientState.
const resetDataReady = (patientId: string | number) => {
  const patientIdStr = String(patientId);
  dataReadyHandled.delete(patientIdStr);
  emrDataLandedSet.delete(patientIdStr);
  dataReadyProcess.delete(patientIdStr);
  clearEmrDataLandTimer(patientIdStr);
};

// Whether a status event belongs to the very process whose data_ready is currently latched. Unknown on
// either side counts as "not the same process", so an event that carries no id still resets -- matching the
// behaviour before the guard existed rather than latching indefinitely.
const isLatchedDataReadyProcess = (patientIdStr: string, processId: any): boolean => {
  const latched = dataReadyProcess.get(patientIdStr);
  return !!latched && !!processId && latched === String(processId);
};

// Whether the current refresh for this patient has already signalled data_ready. Consumed by the hook's
// return value (and, downstream, by consumers that render data-first while the summary continues).
const isDataReady = (patientId: string | number): boolean => dataReadyHandled.has(String(patientId));

// Whether the data_ready EMR refetch has actually landed in cache (or hit the safety-valve timeout) for the
// current refresh. Consumers hold their loading placeholder until this flips so they never flash stale data.
const isEmrDataLanded = (patientId: string | number): boolean => emrDataLandedSet.has(String(patientId));

// Keys to invalidate when the refresh completes ("finished"). If data_ready already fired for this
// refresh, buildDataReadyQueryKeys already refetched the EMR/PIF data fresh a moment ago -- invalidating
// those same keys again here would just re-hit every EMR endpoint for data that hasn't changed since.
// Only the summary-owned keys (deliberately excluded from data_ready, repopulated by "finished") need a
// second fetch in that case. When data_ready never fired for this refresh (e.g. an older backend, or a
// dropped signal), fall back to invalidating everything so completion is still correct.
const buildFinishedQueryKeys = (patientId: string | number, alreadyDataReady: boolean) => {
  const summaryOwnedKeys = [
    ["latestSmartNote", patientId],
    ["latestEncounterHistory", patientId],
  ];
  return alreadyDataReady ? summaryOwnedKeys : [...buildDataReadyQueryKeys(patientId), ...summaryOwnedKeys];
};

/**
 * Add completed refresh to localStorage-persisted history
 * Stores completion data for display in Recent Activity dropdown
 * Only adds if patient was initiated by current user (exists in name cache)
 */
const addCompletedRefresh = (refresh: CompletedRefresh) => {
  // Only add to completed refreshes if patient exists in name cache
  // This ensures we only show refreshes initiated by the current user
  if (patientNameCache[refresh.patientId]) {
    completedRefreshProcesses.unshift(refresh); // Add to front (most recent first)
    // Bound the list in place so the module-level array and localStorage stay in step.
    const pruned = pruneCompletedRefreshes(completedRefreshProcesses);
    if (pruned.length !== completedRefreshProcesses.length) {
      completedRefreshProcesses.length = 0;
      completedRefreshProcesses.push(...pruned);
    }
    saveCompletedRefreshes(completedRefreshProcesses); // Persist to localStorage
    notifyStateChange(); // Notify listeners to update UI
  }
};

/**
 * Helper function to clean up all patient-related state
 * Now also saves completion data before cleanup
 */
const cleanupPatientState = (
  patientId: string | number,
  status: CompletedRefresh["status"] = "success",
  finalHistory?: HistoryEntry[],
  errorMessage?: string,
  backendState?: BackendProcessState
) => {
  const patientIdStr = String(patientId);
  const state = globalRefreshState.get(patientIdStr);

  // Save to completed history before deleting
  if (state) {
    // Prefer the final history from the "finished" WebSocket message (includes duration_ms
    // for the last step); otherwise fall back to the in-memory state.
    const historyToSave = finalHistory && Array.isArray(finalHistory) && finalHistory.length > 0 ? finalHistory : state.history;
    const completed: CompletedRefresh = {
      patientId: patientIdStr,
      patientName: state.patientName,
      processId: state.processId,
      startTime: state.startTime,
      completedAt: Date.now(),
      duration: Date.now() - state.startTime,
      history: historyToSave,
      status,
      errorMessage,
      backendState,
    };
    addCompletedRefresh(completed);

    // Report the kickoff-to-completion duration to Sentry. Deliberately here rather than inside
    // addCompletedRefresh, which only records when patientNameCache is populated - this fires for
    // every refresh the tab sees finish, so the Sentry series is not gated on that cache.
    emitRefreshMeasurement(status, completed.duration, completed.processId);
  }

  globalRefreshState.delete(patientIdStr);
  lastFetchTimeMap.delete(patientIdStr);
  inFlightFetchSet.delete(patientIdStr);
  notifyStateChange();
};

/**
 * Updates global refresh state from bulk background process API data
 *
 * USAGE: Called by Patient List components to sync bulk API query results with global state
 *
 * NOTE: This is a FALLBACK mechanism when WebSocket data is unavailable.
 * The primary data source for refresh status is WebSocket messages via handleGlobalSmartSummaryStatus.
 *
 * IMPORTANT: This function should NOT clear all states when processes array is empty,
 * as that may be due to search filtering or pagination, not because there are no active processes.
 * We rely on WebSocket "finished" events or polling to properly clean up completed processes.
 *
 * @param processes - Array of background processes from bulk API response
 * @param patientDataMap - Optional map of patient IDs to patient data (for display names)
 */
export const updateGlobalStateFromBulkData = (processes: any[], patientDataMap?: Map<string, any>) => {
  // Don't clear all states if processes is empty - it might just be due to search/filter
  // Instead, only update/add states for processes that ARE in the response
  if (!processes || processes.length === 0) {
    // No-op: preserve existing states, rely on WebSocket or polling to clean up
    return;
  }

  const activeProcesses = processes.filter(
    (process) =>
      process.is_active &&
      (process.name.includes("Smart Summary") || process.name.includes("Patient Data") || process.name.includes("Refresh"))
  );

  let hasChanges = false;

  // Add/update active processes
  activeProcesses.forEach((process) => {
    const patientId = String(process.patient_id);
    if (!globalRefreshState.has(patientId)) {
      // Get patient name from the map if available
      const patientData = patientDataMap?.get(patientId);
      const patientName = patientData
        ? `${patientData.first_name || ""} ${patientData.last_name || ""}`.trim() || `Patient ${patientId}`
        : getPatientName(patientId); // Use cached name if available

      // Cache the patient name if we got it from patientDataMap
      if (patientData) {
        const fullName = `${patientData.first_name || ""} ${patientData.last_name || ""}`.trim();
        if (fullName) {
          cachePatientName(patientId, fullName);
        }
      }

      globalRefreshState.set(patientId, {
        isRefreshing: true,
        startTime: new Date(process.created_at).getTime(),
        history: process.history || [],
        patientName,
      });
      hasChanges = true;
    } else {
      // Update existing process with new history and other fields
      const existingState = globalRefreshState.get(patientId);
      if (existingState) {
        const newHistory = process.history || [];
        const newStartTime = new Date(process.created_at).getTime();

        // Check if history or startTime changed
        if (!areHistoriesEqual(existingState.history, newHistory) || existingState.startTime !== newStartTime) {
          // Create new state object instead of mutating
          globalRefreshState.set(patientId, {
            ...existingState,
            history: newHistory,
            startTime: newStartTime,
          });
          hasChanges = true;
        }
      }
    }
  });

  // NOTE: We do NOT remove patients that are not in activeProcesses here,
  // because the processes array might be filtered/paginated and not include all active processes.
  // Patient state cleanup should only happen via:
  // 1. WebSocket "finished" event (handleGlobalSmartSummaryStatus)
  // 2. Polling completion detection (startGlobalBatchPolling)
  // 3. Timeout (startGlobalBatchPolling)

  if (hasChanges) {
    notifyStateChange();
  }
};

// Function to manually add a patient to loading state when refresh is clicked
// This avoids unnecessary API calls - we know the process started, so just update the UI
export const addPatientToLoadingState = (patientId: string | number, patientName?: string) => {
  const patientIdStr = String(patientId);
  const existingState = globalRefreshState.get(patientIdStr);

  // A new refresh is starting: clear any data_ready dedup flag from a prior refresh of this patient.
  resetDataReady(patientIdStr);

  // Cache patient name if provided
  if (patientName) {
    cachePatientName(patientIdStr, patientName);
  }

  if (!existingState) {
    // New patient - create new state. History is backend-driven (populated from
    // WebSocket "step" messages); start empty rather than injecting a synthetic step.
    const finalPatientName = patientName || getPatientName(patientIdStr);
    globalRefreshState.set(patientIdStr, {
      isRefreshing: true,
      startTime: Date.now(),
      history: [],
      patientName: finalPatientName,
    });
    notifyStateChange();
  } else {
    // Patient already exists - update patientName if provided
    if (patientName) {
      globalRefreshState.set(patientIdStr, {
        ...existingState,
        patientName,
      });
      notifyStateChange();
    }
  }
};

// Start global batched polling (single interval for all patients)
const startGlobalBatchPolling = (queryClient: any) => {
  // If already polling, don't start another interval
  if (globalBatchPollingIntervalId) return;

  globalBatchPollingIntervalId = setInterval(async () => {
    // Get all patients currently in polling state
    const patientIdsToCheck = Array.from(globalPollingState.keys());

    if (patientIdsToCheck.length === 0) {
      // No patients to poll, stop the interval
      stopGlobalBatchPolling();
      return;
    }

    try {
      // Make single batched API call for all patients
      const response = await fetchBulkActiveBackgroundProcesses(patientIdsToCheck);
      const activeProcesses = response.results || [];

      // Check each patient's status (for...of to support await inside)
      for (const patientId of patientIdsToCheck) {
        const patientIdStr = String(patientId);
        const pollingInfo: any = globalPollingState.get(patientIdStr);

        if (!pollingInfo) continue;

        const elapsed = Date.now() - pollingInfo.startTime;
        const suppressed = pollingInfo.falseFailuresSuppressed === true;

        // Previous behaviour, retained behind the kill switch: treat elapsed wall-clock as a
        // failure verdict. This is what produced red badges for refreshes that were merely queued.
        if (suppressed && elapsed > MAX_POLLING_DURATION) {
          globalPollingState.delete(patientIdStr);
          if (globalRefreshState.has(patientIdStr)) {
            cleanupPatientState(patientIdStr, "timeout");
          }
          toast.warning("Smart summary refresh is taking longer than expected. Please try again later.", {
            position: "top-center",
            autoClose: 5000,
          });
          continue;
        }

        if (!suppressed) {
          // Elapsed time is not evidence of failure -- the job is still active, and the dominant
          // cause of crossing this threshold is queue backlog rather than anything going wrong.
          // Tell the user it is slow (once), and keep polling to a much longer ceiling.
          if (elapsed > MAX_POLLING_DURATION && !pollingInfo.slowNoticeShown) {
            pollingInfo.slowNoticeShown = true;
            toast.info("Smart summary refresh is taking longer than usual. It is still running.", {
              position: "top-center",
              autoClose: 5000,
            });
          }

          // Give up only well past anything observed in production, and even then record it as
          // "unknown" rather than a failure: we stopped watching, we did not see it fail.
          if (elapsed > EXTENDED_MAX_POLLING_DURATION) {
            globalPollingState.delete(patientIdStr);
            if (globalRefreshState.has(patientIdStr)) {
              cleanupPatientState(patientIdStr, "unknown");
            }
            continue;
          }
        }

        // Check if patient has active processes
        const patientProcesses = activeProcesses.filter(
          (process: any) =>
            String(process.patient_id) === patientIdStr &&
            process.is_active &&
            (process.name.includes("Smart Summary") || process.name.includes("Patient Data") || process.name.includes("Refresh"))
        );

        // Recognize a data_ready marker on a still-active process so data-first happens even when
        // the websocket dropped the event (H3). handleDataReady dedups against the same flag the WS path
        // uses, and does NOT complete the refresh -- process disappearance (below) still owns completion.
        const readyProcess = patientProcesses.find(hasDataReadyStep);
        if (readyProcess) {
          handleDataReady(patientIdStr, queryClient, readyProcess.id);
        }

        // If no active processes, refresh is complete (success or error)
        if (patientProcesses.length === 0) {
          // Remove from polling state
          globalPollingState.delete(patientIdStr);

          // Determine success vs error by fetching the most recently completed process
          let completionStatus: "success" | "error" = "success";
          let completionErrorMsg: string | undefined;
          let completionHistory: HistoryEntry[] | undefined;
          // The server-side state of the process we completed on. Carried through to the
          // CompletedRefresh so the Retry gate can distinguish a genuine ERROR from an EXPIRED
          // row that the backend sweep stamped only because the queue was backed up.
          let completionBackendState: BackendProcessState | undefined;

          try {
            const recentResponse = await fetchBackgroundProcesses(patientIdStr, null);
            const recentCompleted = (recentResponse.results || [])
              .filter(
                (p) =>
                  String(p.patient_id) === patientIdStr &&
                  !p.is_active &&
                  (p.name.includes("Smart Summary") || p.name.includes("Patient Data") || p.name.includes("Refresh"))
              )
              .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

            const mostRecent = recentCompleted[0];
            if (mostRecent?.state) {
              completionBackendState = mostRecent.state as BackendProcessState;
            }
            // A failed run is recognised by its state, not by whether a message came with it.
            // Saya's failure path (ErrorBackgroundProcess -> UpdateBackgroundProcess) sets state
            // to ERROR and appends the reason to `history`, but never writes the `error` column --
            // so keying purely off `error` reported real backend failures as successes, and would
            // now also render a green "Completed" row that nonetheless offered Retry.
            // Django's writer does populate `error`, hence both checks.
            const backendReportsFailure = !suppressed && mostRecent?.state === "ERROR";
            if (backendReportsFailure || mostRecent?.error) {
              completionStatus = "error";
              completionErrorMsg = mostRecent?.error ? String(mostRecent.error) : undefined;
            }
            if (mostRecent?.history) {
              completionHistory = mostRecent.history as unknown as HistoryEntry[];
            }
          } catch {
            // If check fails, default to success
          }

          // An EXPIRED row means the backend stopped tracking the process, not that it finished.
          // Reporting that as success is the mirror image of the timeout bug: the sweep stamps
          // EXPIRED off a fixed TTL, so under backlog it fires while the job is still queued.
          // Record it as unknown so we neither claim success nor raise a false failure.
          let finalStatus: CompletedRefresh["status"] = completionStatus;
          if (!suppressed && completionStatus !== "error" && completionBackendState === "EXPIRED") {
            finalStatus = "unknown";
          }

          // Invalidate on any non-error outcome: if the work did land while we were watching, the
          // user should see it. (Unknown is included for exactly that reason.)
          if (finalStatus !== "error") {
            const alreadyDataReady = isDataReady(patientId);
            if (pollingInfo.inValidate) {
              buildFinishedQueryKeys(patientId, alreadyDataReady).forEach((queryKey) => {
                queryClient.invalidateQueries({ queryKey });
              });
            } else if (!alreadyDataReady) {
              queryClient.invalidateQueries({
                queryKey: ["patientData", patientId],
              });
            }
          }

          // Clear global refresh state with appropriate status
          if (globalRefreshState.has(patientIdStr)) {
            cleanupPatientState(patientIdStr, finalStatus, completionHistory, completionErrorMsg, completionBackendState);

            if (finalStatus === "error") {
              toast.error("Smart summary refresh failed. Please try again.", {
                position: "top-center",
                autoClose: 5000,
              });
            } else if (finalStatus === "unknown") {
              toast.info("Smart summary refresh is still processing in the background.", {
                position: "top-center",
                autoClose: 5000,
              });
            } else {
              toast.success("Smart summary refresh completed successfully!", {
                position: "top-center",
                autoClose: 4000,
              });
            }
          }

          // Call completion callback if provided (only on success). Pass whether data_ready already fired for
          // this refresh so consumers can dedup work they've already done data-first (e.g. the PIF vitals fetch).
          // Keyed off finalStatus, not completionStatus: an unresolved ("unknown") refresh must not tell
          // consumers the work finished, or they act data-first on data that has not landed.
          if (finalStatus === "success" && pollingInfo.onRefreshComplete) {
            pollingInfo.onRefreshComplete({ dataReadyFired: isDataReady(patientId) });
          }
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error during global batch polling:", error);
      // Don't stop polling on error, keep trying
    }
  }, POLLING_INTERVAL);
};

// Stop global batched polling
const stopGlobalBatchPolling = () => {
  if (globalBatchPollingIntervalId) {
    clearInterval(globalBatchPollingIntervalId);
    globalBatchPollingIntervalId = null;
  }
};

// Add patient to batch polling
const addPatientToBatchPolling = (
  patientId: string | number,
  queryClient: any,
  options: { inValidate?: boolean; onRefreshComplete?: () => void; falseFailuresSuppressed?: boolean } = {}
) => {
  const patientIdStr = String(patientId);
  const { inValidate = false, onRefreshComplete, falseFailuresSuppressed = false } = options;

  // Prevent duplicate polling for same patient
  if (globalPollingState.has(patientIdStr)) {
    return;
  }

  // Add patient to polling state.
  // The kill-switch value is captured per refresh rather than read globally: this loop is
  // module-level and cannot use React context, and the codebase's convention for non-React code
  // (see convertJsonToHtml) is to take flags as an argument rather than reach for a singleton.
  globalPollingState.set(patientIdStr, {
    startTime: Date.now(),
    inValidate,
    onRefreshComplete,
    falseFailuresSuppressed,
  });

  // Start global batch polling if not already running
  startGlobalBatchPolling(queryClient);
};

// Remove patient from batch polling
const removePatientFromBatchPolling = (patientId: string | number) => {
  const patientIdStr = String(patientId);
  globalPollingState.delete(patientIdStr);

  // If no more patients to poll, stop the global interval
  if (globalPollingState.size === 0) {
    stopGlobalBatchPolling();
  }
};

// Global function to stop polling for a patient (kept for compatibility)
const stopGlobalPolling = (patientId: string | number) => {
  removePatientFromBatchPolling(patientId);
};

// Global function to start polling for a patient (wrapper for batched polling)
const startGlobalPolling = (patientId: string | number, queryClient: any, options: any = {}) => {
  const { inValidate = false, onRefreshComplete, falseFailuresSuppressed = false } = options;

  // Use batched polling instead of individual polling
  addPatientToBatchPolling(patientId, queryClient, { inValidate, onRefreshComplete, falseFailuresSuppressed });
};

/**
 * Fetches and updates background process data from API for a specific patient
 *
 * USAGE: FALLBACK ONLY - Used when WebSocket messages don't include history data
 *
 * This function should only be called when:
 * - WebSocket message is missing the history field
 * - WebSocket is disconnected (handled by polling mechanism)
 *
 * @param patientId - Patient ID to fetch background process for
 * @param _queryClient - React Query client (currently unused in this function)
 */
const fetchAndUpdateBackgroundProcess = async (patientId: string | number, _queryClient: any): Promise<void> => {
  const patientIdStr = String(patientId);

  try {
    // Fetch latest background process data
    const response = await fetchBulkActiveBackgroundProcesses([patientId]);
    const processes = response.results || [];

    // Filter for relevant processes
    const relevantProcesses = processes.filter(
      (process) =>
        String(process.patient_id) === patientIdStr &&
        process.is_active &&
        (process.name.includes("Smart Summary") || process.name.includes("Patient Data") || process.name.includes("Refresh"))
    );

    if (relevantProcesses.length > 0) {
      const process = relevantProcesses[0];
      const existingState = globalRefreshState.get(patientIdStr);

      if (existingState) {
        const newHistory = (process.history || []) as unknown as HistoryEntry[];

        // Only update if history changed
        if (!areHistoriesEqual(existingState.history, newHistory)) {
          globalRefreshState.set(patientIdStr, {
            ...existingState,
            history: newHistory,
            startTime: new Date(process.created_at).getTime(),
          });
          notifyStateChange();
        }
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error fetching background process:", error);
    // Non-critical background update - don't throw
  }
};

/**
 * Throttled version of fetchAndUpdateBackgroundProcess to prevent excessive API calls
 *
 * USAGE: FALLBACK ONLY - Used when WebSocket history is unavailable
 *
 * Implements:
 * - 3-second throttle per patient
 * - In-flight request tracking to prevent concurrent duplicate calls
 * - Automatic cleanup of tracking state
 *
 * @param patientId - Patient ID to fetch background process for
 * @param queryClient - React Query client
 */
const throttledFetchBackgroundProcess = async (patientId: string | number, queryClient: any): Promise<void> => {
  const patientIdStr = String(patientId);
  const now = Date.now();
  const lastFetchTime = lastFetchTimeMap.get(patientIdStr) || 0;

  // Skip if already fetching for this patient (prevents concurrent requests)
  if (inFlightFetchSet.has(patientIdStr)) {
    return;
  }

  // Skip if last fetch was less than throttle interval ago
  if (now - lastFetchTime < FETCH_THROTTLE_INTERVAL) {
    return;
  }

  // Update timestamp immediately AND mark as in-flight
  lastFetchTimeMap.set(patientIdStr, now);
  inFlightFetchSet.add(patientIdStr);

  try {
    await fetchAndUpdateBackgroundProcess(patientId, queryClient);
  } finally {
    // Always remove from in-flight set, even on error
    inFlightFetchSet.delete(patientIdStr);
  }
};

/**
 * Global WebSocket message handler for smart summary status updates
 *
 * This handler implements a WebSocket-first approach for refresh status tracking:
 * 1. PRIMARY: Uses history array from WebSocket messages when available
 * 2. FALLBACK: Falls back to background process API only when WebSocket history is missing
 *
 * This approach eliminates:
 * - Stale UI states from polling delays
 * - Heavy API polling for status updates
 * - Race conditions between WebSocket and API updates
 *
 * Expected WebSocket message structure (NEW):
 * {
 *   event_type: "smart_summary_status",
 *   account_id: <number>,
 *   details: {
 *     patient_id: <number>,           // NOW IN DETAILS (moved from top-level)
 *     status: "started" | "finished" | <intermediate status>,
 *     process_id: <string>,           // NEW: Background process ID
 *     current_step: <string>,         // NEW: Current step name
 *     history: [{ status: string, timestamp: string }, ...]  // Optional, preferred
 *   }
 * }
 *
 * @param message - WebSocket message containing refresh status and optional history
 * @param accountId - Current user's account ID for message validation
 * @param queryClient - React Query client for cache invalidation
 */
const handleGlobalSmartSummaryStatus = (message: any, accountId: string | number, queryClient: any = null) => {
  // Check account ID but process for ANY patient ID
  if (Number(message.account_id) === Number(accountId)) {
    // Extract fields from NEW structure (all inside details object)
    const messagePatientId = message.details?.patient_id;
    const status = message.details?.status;
    const wsHistory = message.details?.history; // History from WebSocket message
    const processId = message.details?.process_id; // NEW: Background process ID
    const currentStep = message.details?.current_step; // NEW: Current step name

    if (status === "started") {
      // Status "started" = process queued (no history yet)
      // eslint-disable-next-line no-console
      console.info("🔄 Smart summary generation queued...", messagePatientId);
      const patientIdStr = String(messagePatientId);

      // Clean up any stale tracking from previous abandoned/errored processes
      lastFetchTimeMap.delete(patientIdStr);
      inFlightFetchSet.delete(patientIdStr);
      // A new refresh is starting: clear any data_ready dedup flag from a prior refresh of this patient.
      // Scoped to the process, because "started" is emitted by the summary stage AFTER the EMR fetch has
      // signalled data_ready for the same process -- resetting unconditionally re-hid the form the MA had
      // just been given and held it there for the whole summary (NKF-1449).
      if (!isLatchedDataReadyProcess(patientIdStr, processId)) {
        resetDataReady(patientIdStr);
      }

      // Initialize empty state; history/current_step are backend-driven and will
      // populate from subsequent "step" WebSocket messages.
      if (messagePatientId) {
        globalRefreshState.set(patientIdStr, {
          isRefreshing: true,
          startTime: Date.now(),
          history: [],
          patientName: globalRefreshState.get(patientIdStr)?.patientName || getPatientName(messagePatientId),
          processId,
          currentStep,
        });
        notifyStateChange();
      }
    } else if (status === "step") {
      // Status "step" = intermediate step with history
      const patientIdStr = String(messagePatientId);
      const existingState = globalRefreshState.get(patientIdStr);

      if (wsHistory && Array.isArray(wsHistory) && wsHistory.length > 0) {
        // Use history from WebSocket message (primary data source)
        if (existingState) {
          // Only update if history changed to avoid unnecessary re-renders
          if (!areHistoriesEqual(existingState.history, wsHistory)) {
            globalRefreshState.set(patientIdStr, {
              ...existingState,
              history: wsHistory,
              processId, // Update process ID
              currentStep, // Update current step
            });
            notifyStateChange();
          }
        } else {
          // Create new state if it doesn't exist
          globalRefreshState.set(patientIdStr, {
            isRefreshing: true,
            startTime: Date.now(),
            history: wsHistory,
            patientName: getPatientName(messagePatientId),
            processId, // Store process ID
            currentStep, // Store current step
          });
          notifyStateChange();
        }
      } else {
        // Fallback: Fetch from API if WebSocket history is unavailable
        if (queryClient && messagePatientId) {
          throttledFetchBackgroundProcess(messagePatientId, queryClient);
        }
      }
    } else if (status === "data_ready") {
      // The EMR data has landed. Refetch it early (deduped) so data-first can happen, but do NOT
      // end the refresh -- "finished" still drives cleanup. Recognized here for cross-page consumers; the
      // per-hook handler does the same for the active patient's page.
      if (messagePatientId) {
        handleDataReady(messagePatientId, queryClient, processId);
      }
    } else if (status === "failed") {
      // Stop any active polling for this patient (WS took over)
      stopGlobalPolling(messagePatientId);

      const wsErrorMessage = message.details?.error ? String(message.details.error) : undefined;
      cleanupPatientState(messagePatientId, "error", wsHistory as HistoryEntry[] | undefined, wsErrorMessage);

      toast.error("Smart summary refresh failed. Please try again.", {
        position: "top-center",
        autoClose: 5000,
      });
    } else if (status === "finished") {
      // Stop any active polling for this patient (WS took over)
      stopGlobalPolling(messagePatientId);

      // Pass the final history (includes duration_ms for the last step) so the
      // completed Recent Activity entry reflects per-step durations from the backend.
      cleanupPatientState(messagePatientId, "success", wsHistory as HistoryEntry[] | undefined);
      // patientData/clinicalFacts/kfreScores are already covered by buildDataReadyQueryKeys -- if
      // data_ready already fired for this refresh, they're already fresh and invalidating them again
      // here would just re-hit those endpoints for no reason.
      if (queryClient && messagePatientId && !isDataReady(messagePatientId)) {
        queryClient.invalidateQueries({
          queryKey: ["patientData", String(messagePatientId)],
        });
        queryClient.invalidateQueries({
          queryKey: ["clinicalFacts", String(messagePatientId)],
        });
        queryClient.invalidateQueries({
          queryKey: ["kfreScores", String(messagePatientId)],
        });
      }
    }
  }
};

/**
 * Removes a specific patient's entry from the completed refreshes list
 */
export const removeFromCompletedRefreshes = (patientId: string) => {
  const idx = completedRefreshProcesses.findIndex((p) => p.patientId === patientId);
  if (idx !== -1) {
    completedRefreshProcesses.splice(idx, 1);
    saveCompletedRefreshes(completedRefreshProcesses);
    notifyStateChange();
  }
};

/**
 * Retries a failed/timed-out refresh for a patient from the Recent Activity dropdown.
 * Removes the patient from completed state, puts them back in loading, and calls the API.
 */
export const retryRefreshForPatient = async (
  patientId: string,
  patientName: string,
  queryClient: any,
  isWebSocketConnected: boolean,
  falseFailuresSuppressed = false
): Promise<void> => {
  removeFromCompletedRefreshes(patientId);
  addPatientToLoadingState(patientId, patientName);

  try {
    await refreshSmartSummary(patientId);

    if (!isWebSocketConnected) {
      startGlobalPolling(patientId, queryClient, { inValidate: true, falseFailuresSuppressed });
    }
  } catch {
    cleanupPatientState(patientId, "error");
    throw new Error("Failed to initiate refresh");
  }
};

export const usePatientDataRefresh = (patientId: string | number, inValidate = false, options: any = {}) => {
  const { onRefreshStart, onRefreshComplete, onDataReady, skipInitialCheck = false } = options;
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [, forceUpdate] = useState(0); // Force re-render when global state changes
  const queryClient = useQueryClient();
  const accountId = useSelector((state: any) => state.auth.account_id);
  // Kill switch. Read here (the hook has context) and handed to the module-level polling
  // loop, which cannot read context itself. Flag off -> the fix is active.
  const flagsData = useContext(FeatureFlagsContext);
  const falseFailuresSuppressed = isFeatureEnabled(flagsData, falseRefreshFailuresSuppressFeature);

  // Note: patientData query is handled by the parent component
  // This hook only manages refresh state and WebSocket events

  const { checkOnce } = useBackgroundProcess();
  const { isConnected: isWebSocketConnected } = useWebSocket();

  // Function to handle refresh completion (shared by WS and polling)
  const handleRefreshCompletion = () => {
    // Invalidate queries to fetch fresh data FIRST. If data_ready already fired for this refresh, the
    // EMR/PIF keys were already refetched fresh at that point -- only the summary-owned keys need a
    // second fetch here, instead of re-hitting every EMR endpoint again.
    const alreadyDataReady = isDataReady(patientId);
    if (inValidate) {
      buildFinishedQueryKeys(patientId, alreadyDataReady).forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
    } else if (!alreadyDataReady) {
      // If inValidate is false, only invalidate patientData
      queryClient.invalidateQueries({
        queryKey: ["patientData", patientId],
      });
    }

    // Clear local refresh state for current patient
    setIsRefreshing(false);

    // Stop global polling if active
    stopGlobalPolling(patientId);

    // Clear global refresh state for current patient (with safety check)
    if (globalRefreshState.has(String(patientId))) {
      cleanupPatientState(patientId);
      // Show success toast for smart summary refresh completion
      toast.success("Smart summary refresh completed successfully!", {
        position: "top-center",
        autoClose: 4000,
      });
    }

    if (onRefreshComplete) {
      // Pass whether data_ready already fired for this refresh so consumers can dedup work they've already
      // done data-first (e.g. the PIF vitals fetch), rather than repeating it on the finished event.
      onRefreshComplete({ dataReadyFired: alreadyDataReady });
    }
  };

  // Subscribe to global state changes
  useEffect(() => {
    const handleGlobalStateChange = () => {
      // Check if global state was cleared for this patient
      const isGloballyRefreshing = globalRefreshState.has(String(patientId));

      // If global state is cleared but local state is still true, clear local state
      // This handles the case where polling completes but local state wasn't cleared
      if (!isGloballyRefreshing && isRefreshing) {
        setIsRefreshing(false);
      }
      forceUpdate(Date.now()); // Force re-render with unique value
    };

    // Add listener for global state changes
    globalStateListeners.add(handleGlobalStateChange);

    // Cleanup listener on unmount
    return () => {
      globalStateListeners.delete(handleGlobalStateChange);
    };
  }, [patientId, isRefreshing]);

  // Fire onDataReady on the false->true edge of the data_ready signal for this patient, once per refresh.
  // handleDataReady always calls notifyStateChange regardless of which transport delivered the signal --
  // WebSocket global listener, per-hook WebSocket handler, or the polling fallback -- so subscribing to
  // globalStateListeners and edge-detecting isDataReady covers all three uniformly. We listen directly rather
  // than keying an effect on a rendered isDataReady value because the hook re-renders via forceUpdate(Date.now())
  // and same-millisecond events could dedup an intermediate render and drop the edge. The latest callback is read
  // from a ref so an inline consumer function doesn't re-subscribe every render; the armed ref tracks the edge and
  // resetDataReady (a new refresh) flips isDataReady back to false, re-arming it for the next data_ready.
  const onDataReadyRef = useRef(onDataReady);
  onDataReadyRef.current = onDataReady;
  useEffect(() => {
    // Start armed so a hook that mounts mid-refresh (navigate away and back during the data_ready window)
    // still refreshes -- a redundant vitals/labs fetch is harmless, stale vitals is the bug we're fixing.
    let armed = true;
    const fireIfReady = () => {
      if (isDataReady(patientId)) {
        if (armed) {
          armed = false;
          onDataReadyRef.current?.();
        }
      } else {
        armed = true; // a new refresh cleared the flag -> re-arm for the next data_ready
      }
    };
    globalStateListeners.add(fireIfReady);
    fireIfReady(); // catch a data_ready that landed between render and subscription
    return () => {
      globalStateListeners.delete(fireIfReady);
    };
  }, [patientId]);

  // Polling-primary self-check: resume polling on mount whenever the patient
  // is refreshing, regardless of WebSocket connectivity. This recovers a "finished" event
  // that was dropped on a healthy socket. Re-runs on WS connectivity changes as well, so a
  // socket flap re-arms the safety net.
  useEffect(() => {
    const isPatientRefreshing = globalRefreshState.has(String(patientId));
    const isAlreadyPolling = globalPollingState.has(String(patientId));

    if (isPatientRefreshing && !isAlreadyPolling) {
      startGlobalPolling(patientId, queryClient, { inValidate, onRefreshComplete, falseFailuresSuppressed });
    }
  }, [patientId, queryClient, inValidate, onRefreshComplete, isWebSocketConnected, falseFailuresSuppressed]);

  const { mutateAsync: refresh } = useMutation({
    mutationFn: () => refreshSmartSummary(patientId),
  });

  // Handle smart summary status updates via WebSocket
  const handleSmartSummaryStatus = (message: any) => {
    // Check account ID but process for ANY patient ID
    if (Number(message.account_id) === Number(accountId)) {
      const messagePatientId = message.details?.patient_id;
      const status = message.details?.status;

      if (status === "finished") {
        // Only invalidate queries if this is the current patient
        if (Number(messagePatientId) === Number(patientId)) {
          // Use the shared completion handler
          handleRefreshCompletion();
        }
      } else if (status === "data_ready") {
        // Refetch EMR data early for the current patient (deduped). The refresh stays in
        // progress -- "finished" still completes it.
        if (Number(messagePatientId) === Number(patientId)) {
          handleDataReady(patientId, queryClient, message.details?.process_id);
        }
      }
    }
  };

  // Subscribe to smart summary refresh events
  useWebSocketHooks({
    patientId,
    accountId,
    onSmartSummaryRefresh: handleSmartSummaryStatus,
  });

  // Note: Global state changes are handled by the global WebSocket listener
  // No need to subscribe to changes here as we check the state directly

  // Effect to populate global state with active processes on page load
  // Skip this check if skipInitialCheck is true (e.g., when parent is managing this)
  useEffect(() => {
    if (skipInitialCheck) return;

    const checkForActiveProcesses = async () => {
      if (patientId && checkOnce) {
        try {
          const activeProcesses = await checkOnce(patientId);

          if (activeProcesses && activeProcesses.length > 0) {
            let hasNewProcesses = false;
            activeProcesses.forEach((process: any) => {
              if (!globalRefreshState.has(String(process.patient_id))) {
                globalRefreshState.set(String(process.patient_id), {
                  isRefreshing: true,
                  startTime: new Date(process.created_at).getTime(),
                  history: process.history || [],
                  patientName: getPatientName(process.patient_id),
                });
                hasNewProcesses = true;
              }
            });

            // Only notify if we actually added new processes
            if (hasNewProcesses) {
              notifyStateChange();
            }
          } else {
            // CRITICAL FIX: Clear global refresh state if no active processes are found
            // This prevents the UI from staying in loading state when network reconnects
            // and no background processes are actually running
            const patientIdStr = String(patientId);
            if (globalRefreshState.has(patientIdStr)) {
              cleanupPatientState(patientIdStr);
            }
          }
        } catch {
          // Silently handle error - background process check is not critical
        }
      }
    };

    checkForActiveProcesses();
  }, [patientId, skipInitialCheck]);

  // Network reconnection is now handled by parent components (Patient List)
  // which use bulk API queries. Individual hooks no longer need to check on reconnect.
  // The bulk query in Patient List will update globalRefreshState via updateGlobalStateFromBulkData()

  // Polling-primary means polling runs continuously while a refresh is in
  // progress and is no longer stopped on WebSocket reconnect. The WS "finished"/"failed"
  // handlers still call stopGlobalPolling when an event arrives, preserving the fast path.

  // Check if there's a global refresh state for this patient
  const hasActiveBackgroundProcess =
    globalRefreshState.has(String(patientId)) && (globalRefreshState.get(String(patientId)) as any).isRefreshing;

  // Combined state: local refresh OR global background process
  const isRefreshingState = isRefreshing || hasActiveBackgroundProcess;

  const refreshPatientData = async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    // A new refresh is starting: clear any data_ready dedup flag from a prior refresh of this patient.
    resetDataReady(patientId);

    // Set global refresh state if not already set
    // Preserve existing patientName if already set by addPatientToLoadingState
    const patientIdStr = String(patientId);
    const existingState = globalRefreshState.get(patientIdStr);
    if (!existingState) {
      // History is backend-driven - start empty until WebSocket provides steps.
      globalRefreshState.set(patientIdStr, {
        isRefreshing: true,
        startTime: Date.now(),
        history: [],
        patientName: getPatientName(patientId),
      });
      notifyStateChange();
    }

    if (onRefreshStart) {
      onRefreshStart();
    }

    try {
      await refresh();

      // Polling-primary: always poll while the refresh is in progress so a
      // dropped WebSocket "finished" event can't strand the UI. WebSocket events remain
      // a faster trigger and call stopGlobalPolling when they arrive.
      startGlobalPolling(patientId, queryClient, { inValidate, onRefreshComplete, falseFailuresSuppressed });
    } catch (error) {
      setIsRefreshing(false);
      // Clear global state on error
      cleanupPatientState(patientId);
      stopGlobalPolling(patientId); // Stop global polling on error
      throw error;
    }
  };

  return {
    isRefreshing: isRefreshingState,
    refreshPatientData,
    hasActiveBackgroundProcess, // Expose this for compatibility
    // True once the EMR data has landed for this patient's current refresh (data_ready), so
    // consumers can render data-first while the summary continues. Cleared on a new refresh.
    dataReady: isDataReady(patientId),
    // True once the data_ready EMR refetch has actually landed in cache (or the safety-valve timeout
    // fired). Consumers hold their loading placeholder until this flips so they never reveal stale data.
    emrDataLanded: isEmrDataLanded(patientId),
  };
};

// Export a function to check if any patient is being refreshed (for compatibility)
export const hasAnyActiveBackgroundProcess = () => {
  return Array.from(globalRefreshState.values()).some((state: any) => state.isRefreshing);
};
// Export a function to get all active processes (for compatibility)
export const getAllActiveProcesses = () => {
  const processes = Array.from(globalRefreshState.entries()).map(([patientId, state]: any) => ({
    patientId,
    isActive: state.isRefreshing,
    name: "Smart Summary Refresh",
    state: "RUNNING",
    history: state.history || [],
    createdAt: new Date(state.startTime).toISOString(),
    patientName: state.patientName,
    processId: state.processId, // Include process ID from WebSocket
    currentStep: state.currentStep, // Include current step from WebSocket
  }));
  return processes;
};

// Hook to get all active processes (for reactive components)
export const useGlobalRefreshState = () => {
  const [forceUpdate, setForceUpdate] = useState(0);

  useEffect(() => {
    const handleGlobalStateChange = () => {
      setForceUpdate(Date.now());
    };

    // Add listener for global state changes
    globalStateListeners.add(handleGlobalStateChange);

    // Cleanup listener on unmount
    return () => {
      globalStateListeners.delete(handleGlobalStateChange);
    };
  }, []);

  return useMemo(() => getAllActiveProcesses(), [forceUpdate]);
};

// Global function to check and update refresh state for a specific patient
// This can be called from anywhere to check for background processes
export const checkAndUpdateGlobalRefreshState = async (patientId: string | number, checkOnce: any, queryClient?: any) => {
  if (!patientId || !checkOnce) return;

  try {
    const activeProcesses = await checkOnce(patientId);

    if (activeProcesses && activeProcesses.length > 0) {
      let hasNewProcesses = false;
      activeProcesses.forEach((process: any) => {
        if (!globalRefreshState.has(String(process.patient_id))) {
          globalRefreshState.set(String(process.patient_id), {
            isRefreshing: true,
            startTime: new Date(process.created_at).getTime(),
            history: process.history || [],
            patientName: getPatientName(process.patient_id),
          });
          hasNewProcesses = true;
        }
        // This refresh was already in flight when the page mounted, so its data_ready websocket event
        // either fired before the listener existed or was addressed to a page that was not open -- and it
        // is never replayed. The step persists on the process history, so recover the latch from there;
        // without it the loader runs to "finished" even though the EMR data landed minutes ago (NKF-1449).
        if (hasDataReadyStep(process)) {
          handleDataReady(String(process.patient_id), queryClient, process.id);
        }
      });

      // Only notify if we actually added new processes
      if (hasNewProcesses) {
        notifyStateChange();
      }
    } else {
      // CRITICAL: Clear global refresh state if no active processes are found
      // This prevents the UI from staying in loading state when network reconnects
      // and no background processes are actually running
      const patientIdStr = String(patientId);
      if (globalRefreshState.has(patientIdStr)) {
        cleanupPatientState(patientIdStr);
      }
    }
  } catch {
    // Silently handle error - background process check is not critical
  }
};

// Function to start polling for multiple patients (used on network reconnect)
// This checks if WebSocket is connected; if not, starts batch polling for all patients with active processes
export const startPollingForPatients = (patientIds: (string | number)[], queryClient: any, isWebSocketConnected: boolean) => {
  // Only start polling if WebSocket is NOT connected
  if (isWebSocketConnected) {
    // WebSocket will handle updates, no need to poll
    return;
  }

  // Start batch polling for all patients with active processes
  patientIds.forEach((patientId) => {
    const patientIdStr = String(patientId);

    // Only add to polling if patient has active process in global state
    if (globalRefreshState.has(patientIdStr)) {
      // Add to batch polling (will be deduplicated if already polling)
      addPatientToBatchPolling(patientId, queryClient, { inValidate: false });
    }
  });
};

// Global WebSocket listener for smart summary events (independent of patient pages)
export const useGlobalSmartSummaryListener = () => {
  const accountId = useSelector((state: any) => state.auth.account_id);
  const queryClient = useQueryClient();

  // Listen to smart summary events globally
  useWebSocketEvent("smart_summary_status", (message: any) => {
    handleGlobalSmartSummaryStatus(message, accountId, queryClient);
  });
};

/**
 * Hook to get all completed refreshes from current session
 * Returns array of completed processes for display in Recent Activity dropdown
 */
export const useCompletedRefreshState = () => {
  const [forceUpdate, setForceUpdate] = useState(0);

  useEffect(() => {
    const handleGlobalStateChange = () => {
      setForceUpdate(Date.now());
    };

    // Add listener for global state changes
    globalStateListeners.add(handleGlobalStateChange);

    // Cleanup listener on unmount
    return () => {
      globalStateListeners.delete(handleGlobalStateChange);
    };
  }, []);

  return useMemo(() => completedRefreshProcesses, [forceUpdate]);
};

/**
 * Clears completed refreshes and patient name cache from localStorage
 * Should be called on user logout to prevent data leakage between sessions
 */
export const clearCompletedRefreshes = () => {
  completedRefreshProcesses.length = 0; // Clear the array
  dataReadyHandled.clear(); // drop data_ready dedup flags on logout
  emrDataLandedSet.clear(); // and the landed latch
  emrDataLandTimers.forEach((timer) => clearTimeout(timer));
  emrDataLandTimers.clear();
  try {
    localStorage.removeItem(COMPLETED_REFRESHES_KEY);
    localStorage.removeItem(PATIENT_NAME_CACHE_KEY); // Clear patient name cache
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to clear completed refreshes from localStorage:", error);
  }

  // Clear in-memory cache as well
  Object.keys(patientNameCache).forEach((key) => delete patientNameCache[key]);

  notifyStateChange(); // Notify listeners to update UI
};
